import { CloseOutlined } from "@ant-design/icons";
import {
  Button,
  Card,
  ConfigProvider,
  Flex,
  Modal,
  Space,
  Spin,
  Table,
  Typography
} from "antd";
import { getDialedNumbers, getVTUser } from "api/services/ContactService";
import { getAllLocations } from "api/services/LocationService";
import {
  KeyFunction,
  TContact,
  TLocation,
  TVTDialedNumberMap,
  TVTUser
} from "api/services/models/types";
import { useTDAuthentication } from "auth/AuthenticationProvider";
import { AxiosError } from "axios";
import ErrorViewer from "components/ErrorViewer";
import { useEffect, useRef, useState } from "react";
import generatePDF from "react-to-pdf";
import { keypad1, keypad2 } from "./Utils";

type TDictationInstructionProps = {
  contact: TContact;
  onClose: () => void;
};

type Steps = {
  title: string;
  list?: string[];
  description: string;
  isPrompt: boolean;
};

const { Paragraph, Text } = Typography;

const columns = [
  {
    title: "Key",
    dataIndex: "key"
  },
  {
    title: "Function",
    dataIndex: "function"
  },
  {
    title: "Description",
    dataIndex: "description"
  }
];

const Header = ({
  header,
  onSubmit,
  onClose
}: {
  header: string;
  onSubmit?: () => void;
  onClose: () => void;
}) => (
  <Flex gap="middle" align="start" justify="space-between">
    <Typography.Title level={4}>{header}</Typography.Title>
    <Space size="small">
      {onSubmit && (
        <Button
          type="primary"
          htmlType="submit"
          className="default-button primary-btn"
          onClick={onSubmit}>
          <span>Print</span>
        </Button>
      )}
      <Button
        style={{ border: "none" }}
        icon={<CloseOutlined style={{ fontSize: ".75rem" }} />}
        onClick={onClose}></Button>
    </Space>
  </Flex>
);

const DictationInstructions = ({ contact, onClose }: Readonly<TDictationInstructionProps>) => {
  const { accountId } = useTDAuthentication();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<AxiosError | null>();
  const [locations, setLocations] = useState<TLocation[]>([]);
  const [vtUser, setVtUser] = useState<TVTUser | null>(null);
  const [dialedNumbersMap, setDialedNumbersMap] = useState<TVTDialedNumberMap[]>();
  const targetRef = useRef(null);

  useEffect(() => {
    if (accountId) {
      setLoading(true);
      setError(null);
      Promise.all([
        contact.isVTUser ? getVTUser(accountId, contact.contactId) : Promise.resolve(null),
        getDialedNumbers()
      ])
        .then(results => {
          setDialedNumbersMap(results[1]);
          setVtUser(results[0]);
        })
        .catch(setError)
        .finally(() => setLoading(false));
    }
  }, []);

  useEffect(() => {
    if (vtUser && !vtUser.defaultLocation) {
      setLoading(true);
      setError(null);
      getAllLocations(accountId!)
        .then(setLocations)
        .catch(setError)
        .finally(() => setLoading(false));
    }
  }, [vtUser]);

  const getPhoneInLocations = (): string[] =>
    locations
      .toSorted((a, b) => a.phoneLocationId - b.phoneLocationId)
      .map(l => `${l.phoneLocationId} for ${l.locationShortName}`);

  const steps: Steps[] = [
    {
      title: "Welcome to the TransDyne dictation System.",
      description: `Please enter your user id(${vtUser?.userId}#) followed by the # sign`,
      isPrompt: true
    },
    {
      title: "Your default password is the same as your user id.",
      description: "Please enter your password followed by the # sign",
      isPrompt: true
    },
    ...(!vtUser?.defaultLocation
      ? [
          {
            title: "Please enter the location Id followed by the # sign.",
            list: getPhoneInLocations(),
            description: "Please enter Location Id followed by # sign",
            isPrompt: true
          }
        ]
      : []),
    ...(!vtUser?.defaultJobType
      ? [
          {
            title: "Please enter Job Type followed by the # sign.",
            description: "Please enter Work Type followed by # sign",
            isPrompt: true
          }
        ]
      : []),
    ...(vtUser?.promptForMRN
      ? [
          {
            title: "Please enter Medical Record Number followed by the # sign.",
            description: "Please enter Medical Record Number followed by # sign",
            isPrompt: true
          }
        ]
      : [])
  ];

  const oratorSteps = [
    {
      title:
        "Please press 2 to record the next dictation then press 5 to save the job when finished or press * for more options.",
      description: `Press 2 and begin recording.`,
      isPrompt: true
    },
    {
      title: "If you need a job confirmation number, press # before proceeding to next step.",
      description: "",
      isPrompt: false
    },
    {
      title: "Press 5 after the dictation is complete.",
      description: "",
      isPrompt: false
    },
    {
      title:
        "Press 1 to save as normal dictation, 2 to save as priority dictation or 3 to return to the current dictation.",
      description: "",
      isPrompt: false
    },
    {
      title: `Dictate Multiple Reports: To dictate another report without hanging up, repeat steps 3-${
        steps.length + 4
      }.`,
      description: "",
      isPrompt: false
    },
    {
      title: "Complete Dictation: To exit system, simply hang up.",
      description: "",
      isPrompt: false
    }
  ];

  const nonOratorSteps = [
    {
      title: `Please press 2 to record the next dictation.`,
      description: `Press 2 and begin recording.`,
      isPrompt: true
    },
    {
      title: `Dictate Multiple Reports: To dictate another report without hanging up, Press 8, and repeat steps 2-${
        steps.length + 1
      }.`,
      description: "",
      isPrompt: false
    },
    {
      title:
        "Complete Dictation: To exit the system, press 5 to manually disconnect or Hang up the handset.",
      description: "",
      isPrompt: true
    }
  ];

  const finalSteps: Steps[] = [
    ...steps,
    ...(vtUser?.isOratorUser ? oratorSteps : nonOratorSteps)
  ];

  const tollFreeNumber = dialedNumbersMap?.find(
    num => num.dialedNumber == vtUser?.dialedNumber
  )?.tollFreeNumber;

  const printInstructions = () => {
    generatePDF(targetRef, {
      filename: `DictationInstructions-${contact.contactName}.pdf`,
      method: "save",
      page: { margin: 10 }
    });
  };

  return (
    <Modal
      width={800}
      maskClosable={false}
      title={null}
      open={true}
      footer={null}
      closeIcon={null}
      destroyOnClose={true}
      style={{ top: 20 }}>
      {loading && (
        <Flex justify="space-around" style={{ width: "100%", textAlign: "center" }}>
          <Spin tip="Loading, please wait..." />
        </Flex>
      )}
      {error && <ErrorViewer error={error} />}
      <Flex justify="flex-end" style={{ width: "100%" }}>
        <Button type="link" onClick={printInstructions}>
          Download
        </Button>
        <Button type="link" onClick={onClose}>
          Close
        </Button>
      </Flex>
      <ConfigProvider
        theme={{
          token: {
            fontSize: 12
          },
          components: {
            Table: {
              padding: 4,
              borderRadius: 0,
              headerBorderRadius: 0
            },
            List: {
              paddingContentVertical: 2
            },
            Typography: {
              margin: 0
            }
          }
        }}>
        <Flex justify="space-around">
          <Card
            ref={targetRef}
            title={"Dictation Instructions for " + contact.contactName}
            styles={{
              body: { padding: "2px" },
              header: { minHeight: 0, padding: "2px" },
              title: { textAlign: "center", padding: 0 }
            }}>
            <Paragraph>{`Dial 1 ${tollFreeNumber} to access dictation system.`}</Paragraph>
            <ol style={{ margin: "0" }}>
              {finalSteps.map(step => (
                <li key={step.title}>
                  <Text>{step.isPrompt ? "You will hear: " : ""}</Text>
                  <Text
                    strong={step.isPrompt}
                    style={step.isPrompt ? { color: "#1868db" } : {}}>
                    {step.title}
                  </Text>
                  {step.list && (
                    <ol style={{ listStyleType: "none", padding: 0, margin: 0 }}>
                      {step.list.map(l => (
                        <li key={l}>{l}</li>
                      ))}
                    </ol>
                  )}
                  <Paragraph style={{ marginBottom: "4px" }}>{step.description}</Paragraph>
                </li>
              ))}
            </ol>

            <Table<KeyFunction>
              columns={columns}
              dataSource={vtUser?.isOratorUser ? keypad1 : keypad2}
              pagination={false}
              style={{ border: "1px solid #77777733", margin: "4px 16px" }}
            />

            <Paragraph style={{ marginTop: 20 }}>
              Note: You can also notify us by emailing to stat@TransDyne.com for STAT
              dictations.
            </Paragraph>
          </Card>
        </Flex>
      </ConfigProvider>
    </Modal>
  );
};

export default DictationInstructions;
